import moment from 'moment';
import _ from 'lodash';
import { timeSeriesXAxis } from '../../../shared/constants/constants';

function getTimeBucketDictionary(bucketTimes, dataLabels, disconnectedGraph) {
    const defaultSeriesDataValues = dataLabels.reduce(
        (result, label) => ({
            ...result,
            [label]: disconnectedGraph ? null : 0,
        }),
        {}
    );

    return bucketTimes.reduce(
        (results, bucket) => ({
            ...results,
            [bucket]: {
                [timeSeriesXAxis]: moment(bucket).valueOf(),
                ...defaultSeriesDataValues,
            },
        }),
        {}
    );
}

function insertSeriesDataIntoTimeBucketDictionary(
    metricSeries,
    metricNameKey,
    metricValueKey,
    datasetKeyedByBucket,
    numberDecimalFormatter
) {
    const timeBucketsDictionary = { ...datasetKeyedByBucket };

    if (!metricSeries) {
        return timeBucketsDictionary;
    }

    metricSeries.forEach((dataPoint) => {
        const dataPointTimeKey = dataPoint[timeSeriesXAxis];
        const originalValueKey = metricValueKey.split(' (')[0];
        const nameKey = dataPoint[metricNameKey] || metricValueKey;

        timeBucketsDictionary[dataPointTimeKey] = {
            ...timeBucketsDictionary[dataPointTimeKey],
            [nameKey]: dataPoint[originalValueKey]
                ? numberDecimalFormatter(dataPoint[originalValueKey])
                : 0,
        };
    });

    return timeBucketsDictionary;
}

export function formatTimeSeriesDataset(
    bucketTimes,
    metricSeries,
    dataLabels,
    metricNameKey,
    metricValueKey,
    numberDecimalFormatter,
    disconnectedGraph = false,
    returnObject = false
) {
    const datasetKeyedByBucket = getTimeBucketDictionary(
        bucketTimes,
        dataLabels,
        disconnectedGraph || !metricSeries?.length
    );

    const withSourceData = insertSeriesDataIntoTimeBucketDictionary(
        metricSeries,
        metricNameKey,
        metricValueKey,
        datasetKeyedByBucket,
        numberDecimalFormatter
    );

    return returnObject ? withSourceData : _.values(withSourceData);
}

export function formatTimeSeriesDatasetMultipleSources(
    metricSource1,
    metricSource2,
    dataLabels1,
    dataLabels2,
    metricNameKey1,
    metricValueKey1,
    metricNameKey2,
    metricValueKey2,
    numberDecimalFormatter,
    disconnectedGraph
) {
    const data1 = formatTimeSeriesDataset(
        metricSource1.bucketTimes,
        metricSource1.metricSeries,
        dataLabels1,
        metricNameKey1,
        metricValueKey1,
        numberDecimalFormatter,
        disconnectedGraph || !metricSource1.metricSeries.length,
        true
    );

    const data2 = formatTimeSeriesDataset(
        metricSource2.bucketTimes,
        metricSource2.metricSeries,
        dataLabels2,
        metricNameKey2,
        metricValueKey2,
        numberDecimalFormatter,
        disconnectedGraph || !metricSource2.metricSeries.length,
        true
    );

    return _(data1).merge(data2).values().value();
}

function getSourceDataForPoints(
    metricSeries,
    metricNames,
    datasetKeyedByBucket,
    numberDecimalFormatter
) {
    const timeBucketsDictionary = { ...datasetKeyedByBucket };

    if (!metricSeries || !metricNames) {
        return timeBucketsDictionary;
    }

    metricSeries.forEach((dataPoint) => {
        metricNames.forEach((metricName) => {
            const dataPointTimeKey = dataPoint[timeSeriesXAxis];

            timeBucketsDictionary[dataPointTimeKey] = {
                ...timeBucketsDictionary[dataPointTimeKey],
                [metricName]: dataPoint[metricName]
                    ? numberDecimalFormatter(dataPoint[metricName])
                    : 0,
            };
        });
    });

    return timeBucketsDictionary;
}

export function formatTimeSeriesDatasetMultiMetric(
    bucketTimes,
    metricSeries,
    metricNames,
    numberDecimalFormatter,
    disconnectedGraph = false,
    returnObject = false
) {
    const datasetKeyedByBucket = getTimeBucketDictionary(
        bucketTimes,
        metricNames,
        disconnectedGraph || !metricSeries.length
    );

    const withSourceData = getSourceDataForPoints(
        metricSeries,
        metricNames,
        datasetKeyedByBucket,
        numberDecimalFormatter
    );

    return returnObject ? withSourceData : _.values(withSourceData);
}

export function formatTimeSeriesDatasetMultipleSourcesMultiMetric(
    metricSource1,
    metricSource2,
    metricNames1,
    metricNames2,
    numberDecimalFormatter,
    disconnectedGraph
) {
    const data1 = formatTimeSeriesDatasetMultiMetric(
        metricSource1.bucketTimes,
        metricSource1.metricSeries,
        metricNames1,
        numberDecimalFormatter,
        disconnectedGraph,
        true
    );

    const data2 = formatTimeSeriesDatasetMultiMetric(
        metricSource2.bucketTimes,
        metricSource2.metricSeries,
        metricNames2,
        numberDecimalFormatter,
        disconnectedGraph,
        true
    );

    return _(data1).merge(data2).values().value();
}
